<!-- Navbar entry point of the MEETING module, for EVERY user: who is online
     right now, one click to call them (or add them to the call in progress),
     plus the device check and the What's new popup. Opens by itself when the
     call window asks for "Add people" (meeting.state.ui.callPanel). -->
<template>
  <div class="cc" style="position: relative;">
    <div class="cc__trigger" :class="{ 'is-live': inCall }"
         :title="inCall ? 'In a call — add people' : 'Call a colleague'" @click="toggle">
      <feather-icon icon="VideoIcon" class="w-5 h-5 navbar-icon" />
      <span v-if="inCall" class="cc__live"></span>
      <span v-else-if="onlineCount" class="cc__badge">{{ onlineCount }}</span>
    </div>

    <div v-if="show" class="cc__backdrop" @click="show = false"></div>

    <transition name="cc-slide">
      <div v-if="show" class="cc__panel">
        <div class="cc__header">
          <div class="cc__header-title">
            <feather-icon :icon="inCall ? 'UserPlusIcon' : 'VideoIcon'" svgClasses="w-4 h-4" />
            <span>{{ inCall ? 'Add people to the call' : 'Calls' }}</span>
          </div>
          <span class="cc__online">{{ onlineCount }} online</span>
          <span class="cc__close" @click="show = false">&times;</span>
        </div>

        <div v-if="people.length > 6" class="cc__search">
          <feather-icon icon="SearchIcon" svgClasses="w-4 h-4" />
          <input v-model="query" type="text" placeholder="Search a colleague" />
        </div>

        <div class="cc__list">
          <div v-if="!socketConnected" class="cc__empty">
            <span class="cc__spinner"></span>
            <strong>Connecting…</strong>
            <small>Looking for who is online.</small>
          </div>
          <div v-else-if="!filtered.length" class="cc__empty">
            <feather-icon icon="UsersIcon" svgClasses="w-7 h-7" />
            <strong>{{ query ? 'No colleague matches' : 'Nobody else is online' }}</strong>
            <small>Colleagues appear here as soon as they have XYLO open.</small>
          </div>
          <div v-for="u in filtered" :key="u.userId" class="cc__row" :class="{ 'is-off': !canCall(u) }">
            <vs-avatar :src="u.avatar" :text="u.username" size="36px" class="cc__avatar" />
            <div class="cc__who">
              <span class="cc__name">{{ u.username }}</span>
              <span class="cc__status" :class="statusClass(u)"><i></i>{{ statusLabel(u) }}</span>
            </div>
            <button class="cc__call" :class="{ 'cc__call--add': inCall }" :disabled="!canCall(u)"
                    :title="buttonTitle(u)" @click="call(u)">
              <feather-icon :icon="inCall ? 'UserPlusIcon' : 'VideoIcon'" svgClasses="w-4 h-4" />
            </button>
          </div>
        </div>

        <div class="cc__footer">
          <button class="cc__foot-btn" @click="deviceCheck">
            <feather-icon icon="SettingsIcon" svgClasses="w-4 h-4" />
            <span>Test mic &amp; camera</span>
          </button>
          <button class="cc__foot-link" @click="whatsNew">What's new</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import meeting from '@/services/meeting.js'

const NOTIF_ASKED_KEY = 'meeting-notif-asked'

export default {
  data () {
    return { show: false, query: '' }
  },
  computed: {
    socketConnected () { return meeting.state.socketConnected },
    inCall () { return meeting.state.room.status === 'active' },
    people () {
      const list = (meeting.state.onlineUsers || []).slice()
      return list.sort((a, b) => {
        const ab = this.inMyCall(a) ? 2 : (a.busy ? 1 : 0)
        const bb = this.inMyCall(b) ? 2 : (b.busy ? 1 : 0)
        if (ab !== bb) return ab - bb
        return String(a.username || '').localeCompare(String(b.username || ''))
      })
    },
    onlineCount () { return (meeting.state.onlineUsers || []).length },
    filtered () {
      const q = this.query.trim().toLowerCase()
      if (!q) return this.people
      return this.people.filter(u => String(u.username || '').toLowerCase().includes(q))
    },
    panelRequested () { return meeting.state.ui.callPanel }
  },
  watch: {
    panelRequested (val) {
      if (!val) return
      meeting.state.ui.callPanel = false
      this.openPanel()
    }
  },
  methods: {
    toggle () {
      if (this.show) { this.show = false; return }
      this.openPanel()
    },
    openPanel () {
      meeting.init() // idempotent — makes sure presence is flowing
      this.query = ''
      this.show = true
      this.maybeAskNotifications()
    },
    // Ask once, on a deliberate click (never by surprise at page load).
    maybeAskNotifications () {
      try {
        if (!('Notification' in window) || Notification.permission !== 'default') return
        if (localStorage.getItem(NOTIF_ASKED_KEY)) return
        localStorage.setItem(NOTIF_ASKED_KEY, '1')
        Notification.requestPermission()
      } catch (e) { /* noop */ }
    },
    inMyCall (u) { return this.inCall && meeting.state.participants.some(p => p.userId === u.userId) },
    isPending (u) { return meeting.state.pendingInvites.some(p => p.userId === u.userId) },
    canCall (u) { return !u.busy && !this.inMyCall(u) && !this.isPending(u) },
    statusLabel (u) {
      if (this.inMyCall(u)) return 'In your call'
      if (this.isPending(u)) return 'Ringing…'
      if (u.busy) return 'In a call'
      return 'Online'
    },
    statusClass (u) {
      if (this.inMyCall(u) || this.isPending(u)) return 'is-mine'
      return u.busy ? 'is-busy' : 'is-online'
    },
    buttonTitle (u) {
      if (this.inMyCall(u)) return 'Already in your call'
      if (this.isPending(u)) return 'Ringing…'
      if (u.busy) return 'In another call'
      return this.inCall ? `Add ${u.username} to the call` : `Call ${u.username}`
    },
    call (u) {
      if (!this.canCall(u)) return
      meeting.callOrInvite({ userId: u.userId, username: u.username, avatar: u.avatar })
      this.show = false
    },
    deviceCheck () {
      this.show = false
      meeting.openDeviceCheck()
    },
    whatsNew () {
      this.show = false
      meeting.openWhatsNew()
    }
  }
}
</script>

<style scoped>
.cc__trigger {
  position: relative; display: inline-flex; align-items: center; cursor: pointer;
  padding: 6px; margin-right: 6px; border-radius: 8px; color: #64748b;
}
.cc__trigger:hover { color: #7367f0; background: rgba(115,103,240,.08); }
.cc__badge {
  position: absolute; top: 0; right: 0; min-width: 16px; height: 16px; padding: 0 4px;
  border-radius: 999px; background: #28c76f; color: #fff; font-size: 10px; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center; line-height: 1;
}
.cc__live {
  position: absolute; top: 3px; right: 3px; width: 10px; height: 10px; border-radius: 50%;
  background: #ea5455; box-shadow: 0 0 0 0 rgba(234,84,85,.6); animation: cc-live 1.4s infinite;
}
.cc__trigger.is-live { color: #ea5455; }
@keyframes cc-live { 70% { box-shadow: 0 0 0 7px rgba(234,84,85,0); } 100% { box-shadow: 0 0 0 0 rgba(234,84,85,0); } }

.cc__backdrop { position: fixed; inset: 0; z-index: 99998; }
.cc__panel {
  position: fixed; top: 58px; right: 14px; width: 360px; max-width: calc(100vw - 20px);
  background: #fff; border-radius: 14px; overflow: hidden; z-index: 99999;
  box-shadow: 0 12px 40px rgba(0,0,0,.14), 0 2px 8px rgba(0,0,0,.08);
  display: flex; flex-direction: column; max-height: calc(100vh - 80px);
}
.cc__header {
  display: flex; align-items: center; gap: 10px; padding: 13px 16px 12px;
  background: #1e293b; color: #fff; font-size: 13px; font-weight: 600;
}
.cc__header-title { display: flex; align-items: center; gap: 8px; }
.cc__online { margin-left: auto; font-size: 11px; font-weight: 500; color: rgba(255,255,255,.65); }
.cc__close { cursor: pointer; font-size: 20px; line-height: 1; color: rgba(255,255,255,.7); }
.cc__close:hover { color: #fff; }

.cc__search { display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-bottom: 1px solid #f1f3f6; color: #94a3b8; }
.cc__search input { flex: 1; border: none; outline: none; font-size: 13px; color: #1e293b; background: transparent; }

.cc__list { overflow-y: auto; padding: 6px 0; min-height: 120px; }
.cc__row { display: flex; align-items: center; gap: 12px; padding: 9px 16px; }
.cc__row:hover { background: #f8fafc; }
.cc__row.is-off .cc__name { color: #64748b; }
.cc__avatar { margin: 0 !important; flex-shrink: 0; }
.cc__who { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.cc__name { font-size: 14px; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cc__status { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; color: #64748b; }
.cc__status i { width: 7px; height: 7px; border-radius: 50%; background: #94a3b8; display: inline-block; }
.cc__status.is-online i { background: #28c76f; }
.cc__status.is-busy i { background: #ff9f43; }
.cc__status.is-mine i { background: #7367f0; }
.cc__call {
  flex-shrink: 0; width: 36px; height: 36px; border-radius: 50%; border: none; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: #28c76f; color: #fff; box-shadow: 0 4px 12px rgba(40,199,111,.35);
  transition: transform .12s ease, box-shadow .12s ease;
}
.cc__call:hover:not(:disabled) { transform: scale(1.08); box-shadow: 0 6px 16px rgba(40,199,111,.45); }
.cc__call--add { background: #7367f0; box-shadow: 0 4px 12px rgba(115,103,240,.35); }
.cc__call--add:hover:not(:disabled) { box-shadow: 0 6px 16px rgba(115,103,240,.45); }
.cc__call:disabled { background: #e2e8f0; color: #94a3b8; box-shadow: none; cursor: not-allowed; }

.cc__empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 28px 20px; text-align: center; color: #94a3b8; }
.cc__empty strong { color: #1e293b; font-size: 13px; }
.cc__empty small { font-size: 12px; }
.cc__spinner { width: 22px; height: 22px; border-radius: 50%; border: 2px solid #e2e8f0; border-top-color: #7367f0; animation: cc-spin .9s linear infinite; }
@keyframes cc-spin { to { transform: rotate(360deg); } }

.cc__footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 14px; border-top: 1px solid #f1f3f6; background: #fafbfc; }
.cc__foot-btn {
  display: inline-flex; align-items: center; gap: 7px; padding: 7px 12px; border-radius: 8px; border: 1px solid #dfe5ee;
  background: #fff; color: #1e293b; font-size: 12px; font-weight: 600; cursor: pointer;
}
.cc__foot-btn:hover { border-color: #7367f0; color: #7367f0; }
.cc__foot-link { border: none; background: transparent; color: #7367f0; font-size: 12px; font-weight: 600; cursor: pointer; }
.cc__foot-link:hover { text-decoration: underline; }

.cc-slide-enter-active, .cc-slide-leave-active { transition: opacity .18s ease, transform .18s ease; }
.cc-slide-enter, .cc-slide-leave-to { opacity: 0; transform: translateY(-8px) scale(.98); }
</style>
